// src/packages/care/prediction/cycleProjection.ts
//
// Shared projection geometry: given an estimate of a patient's cycle-length distribution
// (mean, uncertainty in the mean, and within-woman variance), project the next-period onset,
// ovulation, and the ASRM fertile window — with uncertainty propagated into every interval.
//
// This is the part that is IDENTICAL across models. Each model differs only in HOW it produces
// the CycleEstimate (Bayesian posterior vs naive arithmetic average); the projection below is
// shared so the two never drift.

import { LUTEAL_PHASE, FERTILE_WINDOW } from "./knowledgeBase";
import { addDays, daysBetween, normalCdf, clamp } from "./cycleStats";
import type {
  DateDistribution,
  DateInterval,
  FertileDay,
  LocalDate,
} from "./fertileWindow.types";

// --- Projection hyperparameters (not clinical priors) -----------------------------------

/** Credible-interval mass reported for ovulation and next-period onset. */
export const INTERVAL_CONFIDENCE = 0.8;
/** Two-sided z-score for INTERVAL_CONFIDENCE (0.8 → 1.2816). */
const INTERVAL_Z = 1.2816;
/**
 * Grace before a merely-late period is treated as a skipped cycle: a period up to about a
 * week late is still "the current cycle". Irregular cyclers get more grace via their wider SD.
 */
const LATE_PERIOD_GRACE_DAYS = 7;

/** A model's estimate of the patient's cycle-length distribution. */
export type CycleEstimate = {
  /** Expected cycle length (days). */
  cycleMeanDays: number;
  /** Variance of the mean estimate (uncertainty in μ itself). */
  cycleMeanVar: number;
  /** Within-woman cycle-to-cycle variance (σ²). */
  withinVar: number;
};

export type CycleProjection = {
  nextPeriod: DateInterval;
  ovulation: DateDistribution | null;
  fertileDays: FertileDay[];
};

/**
 * Project the fertile window from a cycle-length estimate.
 * @param personalized when false, only the broad next-period onset is returned (no
 *   false-precise ovulation/fertile window) — used for the sparse-history tier.
 */
export function projectFertileWindow(
  estimate: CycleEstimate,
  lastStart: LocalDate,
  today: LocalDate,
  personalized: boolean,
): CycleProjection {
  const {
    cycleMeanDays: postMean,
    cycleMeanVar: postVarMean,
    withinVar,
  } = estimate;

  // How many cycles ahead is the next onset after today? A cycle only counts as elapsed once
  // today is past its onset plus a grace band, so a merely-late current period isn't skipped.
  const gapToToday = daysBetween(lastStart, today);
  const oneCycleSd = Math.sqrt(postVarMean + withinVar);
  const graceDays = Math.max(LATE_PERIOD_GRACE_DAYS, INTERVAL_Z * oneCycleSd);
  const cyclesAhead = Math.max(
    1,
    Math.floor((gapToToday - graceDays) / postMean) + 1,
  );

  // Predictive distribution for the onset, k cycles ahead. Var(sum of k cycles sharing an
  // uncertain common mean) = k²·Var(μ) + k·σ².
  const onsetMeanDays = cyclesAhead * postMean;
  const onsetVar =
    cyclesAhead * cyclesAhead * postVarMean + cyclesAhead * withinVar;
  const onsetSd = Math.sqrt(onsetVar);

  // Ovulation = onset − luteal length (luteal is a distribution).
  const ovulationMeanDays = onsetMeanDays - LUTEAL_PHASE.meanDays;
  const ovulationSd = Math.sqrt(onsetVar + LUTEAL_PHASE.sdDays ** 2);

  // Next-period onset as an INTERVAL of likely onset dates (uncertainty band, not a bleed span).
  const nextPeriod: DateInterval = {
    start: addDays(lastStart, onsetMeanDays - INTERVAL_Z * onsetSd),
    end: addDays(lastStart, onsetMeanDays + INTERVAL_Z * onsetSd),
  };

  if (!personalized) {
    return { nextPeriod, ovulation: null, fertileDays: [] };
  }

  const ovulation: DateDistribution = {
    mostLikely: addDays(lastStart, ovulationMeanDays),
    interval: {
      start: addDays(lastStart, ovulationMeanDays - INTERVAL_Z * ovulationSd),
      end: addDays(lastStart, ovulationMeanDays + INTERVAL_Z * ovulationSd),
    },
    intervalConfidence: INTERVAL_CONFIDENCE,
  };

  // Per-day probability = probability the date falls within [ovulation − 5, ovulation] given
  // the ovulation posterior (d ∈ [ov−5, ov] ⇔ ov ∈ [d, d+5]). ASRM 6-day window.
  const windowSpan = FERTILE_WINDOW.windowLengthDays - 1; // 5
  const ovulationDayIndex = Math.round(ovulationMeanDays);
  const fertileDays: FertileDay[] = FERTILE_WINDOW.dayCategories.map(
    ({ dayOffset, category }) => {
      const dayIndex = ovulationDayIndex + dayOffset;
      const probability = clamp(
        normalCdf((dayIndex + windowSpan - ovulationMeanDays) / ovulationSd) -
          normalCdf((dayIndex - ovulationMeanDays) / ovulationSd),
        0,
        1,
      );
      return {
        date: addDays(lastStart, dayIndex),
        probability: Math.round(probability * 1000) / 1000,
        category,
      };
    },
  );

  return { nextPeriod, ovulation, fertileDays };
}
